import type { MissingField } from "@platform/plan-grid-core";

export const COVERAGE_TOOLTIP_MESSAGES = {
  ELIGIBILITY_CLASS_REQUIRED:
    "At least one eligibility class must be assigned to this coverage before marking it complete",
  REQUIRED_FIELDS_MISSING:
    "The following required fields must be filled in before marking this coverage complete:",
  DUPLICATE_CURRENT_POLICY:
    "Two or more plan groups share the same Current Policy. Each plan group must have a different policy before marking this coverage complete.",
} as const;

export interface CompletionGateParams {
  isComplete: boolean;
  hasEligibilityClasses: boolean;
  hasAllRequiredFields: boolean;
  hasDuplicateCurrentPolicy: boolean;
}

export interface CompletionGate {
  isMissingEligibilityClasses: boolean;
  isMissingRequiredFields: boolean;
  isDuplicateCurrentPolicy: boolean;
  /** True when any condition prevents marking the coverage complete. */
  isBlocked: boolean;
}

/**
 * Computes the conditions that block marking a coverage complete. Extracted so
 * the completion controls stay simple (and under the complexity budget).
 */
export const computeCompletionGate = ({
  isComplete,
  hasEligibilityClasses,
  hasAllRequiredFields,
  hasDuplicateCurrentPolicy,
}: CompletionGateParams): CompletionGate => {
  const isMissingEligibilityClasses = !isComplete && !hasEligibilityClasses;
  const isMissingRequiredFields = !isComplete && !hasAllRequiredFields;
  const isDuplicateCurrentPolicy = !isComplete && hasDuplicateCurrentPolicy;

  return {
    isMissingEligibilityClasses,
    isMissingRequiredFields,
    isDuplicateCurrentPolicy,
    isBlocked:
      isMissingEligibilityClasses ||
      isMissingRequiredFields ||
      isDuplicateCurrentPolicy,
  };
};

export interface BuildCoverageCompletionTooltipMessageParams {
  isMissingEligibilityClasses: boolean;
  isMissingRequiredFields: boolean;
  isDuplicateCurrentPolicy?: boolean;
  missingFields?: MissingField[];
}

export const buildCoverageCompletionTooltipMessage = ({
  isMissingEligibilityClasses,
  isMissingRequiredFields,
  isDuplicateCurrentPolicy = false,
  missingFields = [],
}: BuildCoverageCompletionTooltipMessageParams): string => {
  if (isDuplicateCurrentPolicy) {
    return COVERAGE_TOOLTIP_MESSAGES.DUPLICATE_CURRENT_POLICY;
  }

  if (isMissingEligibilityClasses) {
    return COVERAGE_TOOLTIP_MESSAGES.ELIGIBILITY_CLASS_REQUIRED;
  }

  if (!isMissingRequiredFields) {
    return "";
  }

  if (missingFields.length === 0) {
    return COVERAGE_TOOLTIP_MESSAGES.REQUIRED_FIELDS_MISSING;
  }

  const fieldLabels = missingFields
    .slice(0, 5)
    .map((field) => `• ${field.label}`)
    .join("\n");
  const remaining = missingFields.length - 5;
  const suffix = remaining > 0 ? `\n...and ${remaining} more` : "";

  return `${COVERAGE_TOOLTIP_MESSAGES.REQUIRED_FIELDS_MISSING}\n${fieldLabels}${suffix}`;
};
